/**
 * Inspector mode state (TEC-465 P2/P3).
 *
 * Zero-overhead contract: when inspector mode is off, `<Msg>` reads `enabled`
 * from this context (a single boolean) and renders the message untouched: no
 * DOM markers, no usage recording, no observers. When there is no provider at
 * all (the normal production tree), `useI18nInspector()` returns a frozen
 * DISABLED constant, so the cost is one context read and a falsy branch.
 *
 * Inspector mode only becomes *available* for reviewers (gated upstream by the
 * `available` prop, which the host computes from its auth + a kill flag), and
 * only becomes *enabled* when a reviewer toggles it on. It can be killed
 * instantly by forcing `available={false}`.
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'i18n-inspector-enabled';
const FOCUS_QUERY_PARAM = 'i18nInspect';

export interface InspectorState {
  /** Reviewer + not killed: the toggle may be shown. */
  available: boolean;
  /** available && toggled on: markers render and edits are possible. */
  enabled: boolean;
  /** Message ID to focus, from a `?i18nInspect=` deep link (catalogue → context). */
  focusId: string | null;
  setEnabled: (on: boolean) => void;
  setFocusId: (id: string | null) => void;
  /** Reviewer switched the page to English (E key / panel button): `<Msg>` renders the source. */
  showEnglish: boolean;
  setShowEnglish: (on: boolean) => void;
  /** English catalogue, loaded once the inspector is on; null until then (or when the host has none). */
  englishMessages: Record<string, string> | null;
}

const DISABLED: InspectorState = {
  available: false,
  enabled: false,
  focusId: null,
  setEnabled: () => {},
  setFocusId: () => {},
  showEnglish: false,
  setShowEnglish: () => {},
  englishMessages: null,
};

const InspectorContext = createContext<InspectorState | undefined>(undefined);

export function InspectorProvider({
  available,
  loadEnglishMessages,
  children,
}: {
  available: boolean;
  /** Host-supplied loader for the English catalogue (the overlay's "English original"). */
  loadEnglishMessages?: () => Promise<Record<string, string>>;
  children: React.ReactNode;
}): React.ReactElement {
  const [toggled, setToggled] = useState(false);
  const [focusId, setFocusId] = useState<string | null>(null);
  const [showEnglish, setShowEnglish] = useState(false);
  const [englishMessages, setEnglishMessages] = useState<Record<string, string> | null>(null);

  useEffect(() => {
    if (!available) {
      setToggled(false);
      return;
    }
    try {
      const params = new URLSearchParams(window.location.search);
      const focus = params.get(FOCUS_QUERY_PARAM);
      if (focus) {
        // Arriving from a catalogue "Open in context" deep link: focus + auto-on.
        setFocusId(focus);
        setToggled(true);
        return;
      }
      if (localStorage.getItem(STORAGE_KEY) === '1') setToggled(true);
    } catch {
      /* localStorage / URL unavailable: stay off. */
    }
  }, [available]);

  const setEnabled = (on: boolean): void => {
    setToggled(on);
    try {
      localStorage.setItem(STORAGE_KEY, on ? '1' : '0');
    } catch {
      /* ignore */
    }
  };

  const enabled = available && toggled;

  useEffect(() => {
    if (!enabled || englishMessages || !loadEnglishMessages) return;
    let cancelled = false;
    loadEnglishMessages()
      .then((loaded) => {
        if (!cancelled) setEnglishMessages(loaded);
      })
      .catch(() => {
        /* the popover then says the English isn't available */
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, englishMessages, loadEnglishMessages]);

  const value = useMemo<InspectorState>(
    () => ({
      available,
      enabled,
      focusId,
      setEnabled,
      setFocusId,
      showEnglish: enabled && showEnglish,
      setShowEnglish,
      englishMessages,
    }),
    [available, enabled, focusId, showEnglish, englishMessages],
  );

  return <InspectorContext.Provider value={value}>{children}</InspectorContext.Provider>;
}

export function useI18nInspector(): InspectorState {
  return useContext(InspectorContext) ?? DISABLED;
}

/** True when an Inspector(Static)Provider is already mounted above this point in the tree. */
export function useHasInspectorProvider(): boolean {
  return useContext(InspectorContext) !== undefined;
}

/**
 * SSR-only counterpart to `InspectorProvider`, for routes that never hydrate (see
 * StaticInspectorMount.tsx). `InspectorProvider`'s `toggled` state only ever flips
 * inside a `useEffect`, which never runs without hydration, so `enabled` would stay
 * false forever there. This is deterministic at render time instead: `enabled` is
 * passed in directly, already fully resolved server-side, no state/effects needed.
 */
export function StaticInspectorProvider({
  enabled,
  children,
}: {
  enabled: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  const value = useMemo<InspectorState>(
    () => ({
      available: enabled,
      enabled,
      focusId: null,
      setEnabled: () => {},
      setFocusId: () => {},
      showEnglish: false,
      setShowEnglish: () => {},
      englishMessages: null,
    }),
    [enabled],
  );
  return <InspectorContext.Provider value={value}>{children}</InspectorContext.Provider>;
}
